import { useMutation } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import { debounce } from 'lodash';
import { adminAuthorFiltersAtom, adminAuthorsQueryAtom } from 'r/atoms';
import { Search, X, ChevronDown, ChevronUp } from 'lucide-react';
import type { Issuer, IssuerWithPerformanceMetrics, Profile } from 'r/types';
import { Avatar, SpinnerCircle } from 'r/ui';
import { showNotification, useStateReducer } from 'r/utils';
import { fetchApiRaw, PUT_ADMIN_INFO } from 'r/utils/fetchApiRaw';
import { useCallback, useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

function PaginationControls({
  page,
  total,
  limit,
  onPageChange,
}: {
  page: number;
  total: number;
  limit: number;
  onPageChange: (p: number) => void;
}) {
  const totalPages = Math.ceil(total / limit);

  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-center gap-4 py-4">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(Math.max(0, page - 1))}
        disabled={page === 0}
      >
        Prev
      </Button>
      <span className="text-sm text-muted-foreground">
        Page {page + 1} of {totalPages}
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
        disabled={page >= totalPages - 1}
      >
        Next
      </Button>
    </div>
  );
}

const SORT_OPTIONS = [
  { value: 'createdAt:desc', label: 'Created At (Newest First)' },
  { value: 'createdAt:asc', label: 'Created At (Oldest First)' },
  { value: 'predictionsCount:desc', label: 'Predictions (High → Low)' },
  { value: 'predictionsCount:asc', label: 'Predictions (Low → High)' },
  { value: 'username:asc', label: 'Username (A → Z)' },
  { value: 'username:desc', label: 'Username (Z → A)' },
];

function formatDate(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function AuthorsTab({ profile }: { profile: Profile }) {
  const [filters, setFilters] = useAtom(adminAuthorFiltersAtom);
  const {
    data: queryData,
    isLoading,
    refetch: updateAuthors,
  } = useAtomValue(adminAuthorsQueryAtom);

  const authors = queryData?.data || [];
  const total = queryData?.total || 0;

  const [localSearch, setLocalSearch] = useState(filters.search);

  const debouncedSetSearch = useCallback(
    debounce((searchValue: string) => {
      if (searchValue !== filters.search) {
        setFilters((prev) => ({ ...prev, search: searchValue, page: 0 }));
      }
    }, 750),
    [filters.search, setFilters],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setLocalSearch(value);
      debouncedSetSearch(value);
    },
    [debouncedSetSearch],
  );

  useEffect(() => {
    if (filters.search !== localSearch) {
      setLocalSearch(filters.search);
    }
  }, [filters.search]);

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, []);

  const handleSortChange = useCallback(
    (value: string) => {
      setFilters((prev) => ({ ...prev, sortBy: value, page: 0 }));
    },
    [setFilters],
  );

  const handleClearSearch = useCallback(() => {
    setLocalSearch('');
    setFilters((prev) => ({ ...prev, search: '', page: 0 }));
    debouncedSetSearch.cancel();
  }, [setFilters, debouncedSetSearch]);

  const updateAuthorMutation = useMutation({
    mutationFn: async ({ data }: { data: Issuer }) => {
      return await fetchApiRaw(
        PUT_ADMIN_INFO,
        {
          body: { userId: data.id, adminInfo: data.adminInfo },
        },
        profile.accessToken,
      );
    },
    onSuccess: () => {
      showNotification('success', 'Author updated');
      updateAuthors();
    },
    onError: () => {
      showNotification('error', 'Failed to update author');
    },
  });

  if (isLoading && authors.length === 0)
    return (
      <div className="flex justify-center p-10">
        <SpinnerCircle size={24} color="var(--muted-foreground)" />
      </div>
    );

  return (
    <div className="w-7xl py-6 space-y-4">
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search Input */}
        <div className="relative w-full max-w-md">
          <Input
            type="text"
            placeholder="Search Author or Twitter name"
            value={localSearch}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pr-10"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            <Search className="w-4 h-4" />
          </div>
          {localSearch && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearSearch}
              className="absolute right-8 top-1/2 -translate-y-1/2 h-auto p-0 hover:text-destructive"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Sort Selector */}
        <Select value={filters.sortBy} onValueChange={handleSortChange}>
          <SelectTrigger className="w-full md:w-64">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* KOL Filter Toggle */}
      <div className="flex items-center gap-3 p-4 bg-card rounded-xl border">
        <Checkbox
          id="kol-filter"
          checked={filters.showLeaderboardOnly}
          onCheckedChange={(checked) =>
            setFilters((prev) => ({
              ...prev,
              showLeaderboardOnly: checked === true,
              page: 0,
            }))
          }
        />
        <label
          htmlFor="kol-filter"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
        >
          Show Leaderboard Only (KOLs)
        </label>
      </div>

      {/* Desktop Table */}
      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full text-left">
          <thead className="bg-muted/50 text-muted-foreground text-sm uppercase">
            <tr>
              <th className="py-3 px-4 font-medium">
                <div className="flex items-center gap-1">
                  Author
                  {(filters.sortBy === 'username:asc' ||
                    filters.sortBy === 'username:desc') && (
                    <>
                      {filters.sortBy === 'username:asc' ?
                        <ChevronUp className="w-4 h-4" />
                      : <ChevronDown className="w-4 h-4" />}
                    </>
                  )}
                </div>
              </th>
              <th className="py-3 px-4 font-medium">Is KOL</th>
              <th className="py-3 px-4 font-medium">
                <div className="flex items-center gap-1">
                  Predictions
                  {(filters.sortBy === 'predictionsCount:desc' ||
                    filters.sortBy === 'predictionsCount:asc') && (
                    <>
                      {filters.sortBy === 'predictionsCount:desc' ?
                        <ChevronDown className="w-4 h-4" />
                      : <ChevronUp className="w-4 h-4" />}
                    </>
                  )}
                </div>
              </th>
              <th className="py-3 px-4 font-medium">
                <div className="flex items-center gap-1">
                  Created At
                  {(filters.sortBy === 'createdAt:desc' ||
                    filters.sortBy === 'createdAt:asc') && (
                    <>
                      {filters.sortBy === 'createdAt:desc' ?
                        <ChevronDown className="w-4 h-4" />
                      : <ChevronUp className="w-4 h-4" />}
                    </>
                  )}
                </div>
              </th>
              <th className="py-3 px-4 font-medium">Insight</th>
              <th className="py-3 px-4 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {authors.map((author) => (
              <AuthorRow
                key={author.id}
                author={author}
                onSave={(data) => updateAuthorMutation.mutate({ data })}
              />
            ))}
            {authors.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="py-8 text-center text-muted-foreground"
                >
                  {filters.search ?
                    `No authors found for "${filters.search}"`
                  : filters.showLeaderboardOnly ?
                    'No KOL authors found'
                  : 'No authors found'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <PaginationControls
        page={filters.page}
        total={total}
        limit={filters.limit}
        onPageChange={(p) => setFilters((prev) => ({ ...prev, page: p }))}
      />
    </div>
  );
}

function AuthorRow({
  author,
  onSave,
}: {
  author: IssuerWithPerformanceMetrics;
  onSave: (data: Issuer) => void;
}) {
  const [{ isKol, insight, isDirty }, ss] = useStateReducer({
    isKol: author.adminInfo?.isKol || false,
    insight: author.adminInfo?.insight || '',
    isDirty: false,
  });

  const handleSave = () => {
    const data = { ...author };
    data.adminInfo = { isKol, insight };

    onSave(data);
    ss({ isDirty: false });
  };

  return (
    <tr className="border-b hover:bg-muted/50 transition-colors">
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          <Avatar size={32} user={{ id: author.id, avatar: author?.avatar }} />
          <div className="flex flex-col">
            <span className="font-medium">{author.twitter}</span>
            <span className="text-xs text-muted-foreground">
              @{author.username}
            </span>
          </div>
        </div>
      </td>
      <td className="py-4 px-4">
        <Checkbox
          checked={isKol}
          onCheckedChange={(checked) => {
            ss({ isKol: checked === true, isDirty: true });
          }}
        />
      </td>
      <td className="py-4 px-4">
        {author.predictionsCount !== undefined ? author.predictionsCount : 0}
      </td>
      <td className="py-4 px-4 text-muted-foreground">
        {formatDate(author.createdAt)}
      </td>
      <td className="py-4 px-4">
        <Input
          type="text"
          value={insight}
          onChange={(e) => {
            ss({ insight: e.target.value, isDirty: true });
          }}
          placeholder="Add insight..."
          className="max-w-xs"
        />
      </td>
      <td className="py-4 px-4 text-right">
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!isDirty}
          className={cn(!isDirty && 'opacity-50')}
        >
          Save
        </Button>
      </td>
    </tr>
  );
}
